import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  DocumentSnapshot,
  serverTimestamp,
  Query,
  DocumentData,
  collectionGroup,
  getCountFromServer,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '@/lib/firebase';
import type { MerchantWithStats, MerchantFormData, MerchantFilters, LocationFormData, PlanFormData, MerchantBankingInfo } from '@/types/merchant';
import type { MerchantBranding, MerchantPromotionSetting } from '@bevpass/types';
import { subscriptionsService } from './subscriptions.service';

export type MerchantLocation = LocationFormData & { id: string };
export type MerchantPlan = PlanFormData & { id: string };

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const removeUndefinedValues = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(item => removeUndefinedValues(item)) as unknown as T;
  }

  if (value instanceof Date) {
    return value;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(value)) {
      if (val === undefined) {
        continue;
      }

      result[key] = removeUndefinedValues(val);
    }

    return result as unknown as T;
  }

  return value;
};

const prepareBrandingForFirestore = (branding?: MerchantBranding) => {
  if (!branding) {
    return undefined;
  }

  return removeUndefinedValues({
    ...branding,
    updatedAt: branding.updatedAt ?? new Date(),
  });
};

class MerchantService {
  private readonly COLLECTION = 'merchants';
  private readonly LOCATIONS_SUBCOLLECTION = 'locations';
  private readonly PLANS_SUBCOLLECTION = 'subscriptionPlans';

  async getPromotion(merchantId: string, promotionId: string): Promise<MerchantPromotionSetting | null> {
    try {
      const promotionRef = doc(db, this.COLLECTION, merchantId, 'promotions', promotionId);
      const snapshot = await getDoc(promotionRef);
      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data() || {};
      const updatedAt =
        typeof (data.updatedAt as { toDate?: () => Date } | undefined)?.toDate === 'function'
          ? (data.updatedAt as { toDate: () => Date }).toDate()
          : undefined;

      return {
        id: snapshot.id,
        enabled: Boolean(data.enabled),
        updatedAt,
        updatedBy:
          typeof data.updatedBy === 'string' && data.updatedBy.trim()
            ? data.updatedBy
            : null,
        metadata: removeUndefinedValues(data.metadata ?? {}) || undefined,
      };
    } catch (error) {
      console.error('Error fetching merchant promotion:', error);
      throw error;
    }
  }

  async setPromotion(
    merchantId: string,
    promotionId: string,
    payload: { enabled: boolean; metadata?: Record<string, unknown> },
  ): Promise<MerchantPromotionSetting> {
    try {
      const promotionRef = doc(db, this.COLLECTION, merchantId, 'promotions', promotionId);
      const body = removeUndefinedValues({
        promotionId,
        enabled: payload.enabled,
        metadata: payload.enabled ? payload.metadata ?? {} : {},
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid ?? null,
      });

      await setDoc(promotionRef, body, { merge: true });
      const updated = await getDoc(promotionRef);
      if (!updated.exists()) {
        throw new Error('Promotion document missing after update');
      }

      const data = updated.data() || {};

      const updatedAt =
        typeof (data.updatedAt as { toDate?: () => Date } | undefined)?.toDate === 'function'
          ? (data.updatedAt as { toDate: () => Date }).toDate()
          : undefined;

      return {
        id: updated.id,
        enabled: Boolean(data.enabled),
        updatedAt,
        updatedBy:
          typeof data.updatedBy === 'string' && data.updatedBy.trim()
            ? data.updatedBy
            : null,
        metadata: data.metadata ?? null,
      };
    } catch (error) {
      console.error('Error updating merchant promotion:', error);
      throw error;
    }
  }

  // Get all merchants with stats
  async getMerchants(filters?: MerchantFilters): Promise<{
    merchants: MerchantWithStats[];
    lastDoc: DocumentSnapshot | null;
    hasMore: boolean;
  }> {
    try {
      let q: Query<DocumentData> = collection(db, this.COLLECTION);

      // Get current user's custom claims to check permissions
      const currentUser = auth.currentUser;
      if (currentUser) {
        const tokenResult = await currentUser.getIdTokenResult();
        const role = tokenResult.claims.role as string;
        const merchantIds = tokenResult.claims.merchantIds as string[] | undefined;
        
        // Filter merchants based on user role
        if (role === 'merchant_owner' || role === 'merchant_viewer') {
          // Only show merchants the user has access to
          if (merchantIds && merchantIds.length > 0) {
            q = query(q, where('__name__', 'in', merchantIds));
          } else {
            // User has no merchant access
            return {
              merchants: [],
              lastDoc: null,
              hasMore: false
            };
          }
        }
        // bevpass_admin can see all merchants (no filter needed)
      }

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        // Handle pending_review status specifically
        if (filters.status === 'pending_review') {
          q = query(q, where('needsReview', '==', true));
        } else {
          q = query(q, where('status', '==', filters.status));
        }
      }

      if (filters?.category) {
        q = query(q, where('contactInfo.businessType', '==', filters.category));
      }

      // Apply sorting
      const sortField = this.getSortField(filters?.sortBy);
      const sortDirection = filters?.sortOrder || 'desc';
      q = query(q, orderBy(sortField, sortDirection));

      // Apply pagination
      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      const merchants: MerchantWithStats[] = [];

      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        // Get stats for each merchant (in production, these would be aggregated)
        const stats = await this.getMerchantStats(doc.id);
        
        merchants.push({
          id: doc.id,
          ...data,
          ...stats,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as MerchantWithStats);
      }

      // Filter by search term (client-side for now)
      let filteredMerchants = merchants;
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filteredMerchants = merchants.filter(m => 
          m.name.toLowerCase().includes(searchLower) ||
          m.description?.toLowerCase().includes(searchLower) ||
          m.contactInfo?.email?.toLowerCase().includes(searchLower)
        );
      }

      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      const hasMore = snapshot.docs.length === (filters?.limit || 20);

      return { merchants: filteredMerchants, lastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching merchants:', error);
      throw error;
    }
  }

  // Get single merchant by ID
  async getMerchantById(id: string): Promise<MerchantWithStats | null> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      const stats = await this.getMerchantStats(id);

      return {
        id: docSnap.id,
        ...data,
        ...stats,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as MerchantWithStats;
    } catch (error) {
      console.error('Error fetching merchant:', error);
      throw error;
    }
  }

  // Create new merchant
  async createMerchant(data: MerchantFormData): Promise<string> {
    try {
      const { branding, status, ...formValues } = data;

      const merchantData = {
        ...formValues,
        status: status ?? 'pending_review',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        settings: {
          isLive: data.isLive ?? false,
          allowMultipleLocations: data.allowMultipleLocations ?? false,
          requireLocationCheck: data.requireLocationCheck ?? false,
        },
        contactInfo: {
          email: data.email,
          phone: data.phone,
          website: data.website,
          businessType: data.category,
          businessName: data.businessName,
          taxId: data.taxId,
        },
        branding: prepareBrandingForFirestore(branding),
      };

      const cleanedData = removeUndefinedValues(merchantData);

      const docRef = await addDoc(collection(db, this.COLLECTION), cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating merchant:', error);
      throw error;
    }
  }

  // Update merchant
  async updateMerchant(id: string, data: Partial<MerchantFormData>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      
      const { branding, ...updateValues } = data;

      const updateData: Record<string, unknown> = {
        ...updateValues,
        updatedAt: serverTimestamp(),
      };

      if (branding) {
        updateData.branding = prepareBrandingForFirestore(branding);
      }

      // Handle nested objects properly
      if (data.email || data.phone || data.website || data.category || data.businessName || data.taxId) {
        updateData.contactInfo = {
          email: data.email,
          phone: data.phone,
          website: data.website,
          businessType: data.category,
          businessName: data.businessName,
          taxId: data.taxId,
        };
      }
      
      if (data.isLive !== undefined || data.allowMultipleLocations !== undefined || 
          data.requireLocationCheck !== undefined) {
        updateData.settings = {
          isLive: data.isLive,
          allowMultipleLocations: data.allowMultipleLocations,
          requireLocationCheck: data.requireLocationCheck,
        };
      }

      const cleanedUpdateData = removeUndefinedValues(updateData);

      await updateDoc(docRef, cleanedUpdateData);
    } catch (error) {
      console.error('Error updating merchant:', error);
      throw error;
    }
  }

  // Delete merchant
  async deleteMerchant(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION, id));
    } catch (error) {
      console.error('Error deleting merchant:', error);
      throw error;
    }
  }

  // Approve merchant
  async approveMerchant(merchantId: string, notes?: string): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      // Call the Firebase function to approve the merchant
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('@/lib/firebase');
      const approveMerchantDirect = httpsCallable(functions, 'approveMerchantDirect');
      
      const result = await approveMerchantDirect({
        merchantId,
        notes
      });

      console.log('Merchant approved:', result);
    } catch (error) {
      console.error('Error approving merchant:', error);
      throw error;
    }
  }

  // Get merchant stats (aggregated from real data)
  private async getMerchantStats(merchantId: string): Promise<Partial<MerchantWithStats>> {
    try {
      // Locations count
      const locationsRef = collection(db, this.COLLECTION, merchantId, this.LOCATIONS_SUBCOLLECTION);
      const locationsCountSnap = await getCountFromServer(query(locationsRef));
      const totalLocations = locationsCountSnap.data().count || 0;

      // Subscription stats (MRR + counts)
      const overall = await subscriptionsService.getOverallStats(merchantId);
      const activeSubscribers = overall.activeCount || 0;
      const monthlyRevenue = overall.totalRevenue || 0; // cents
      const totalSubscribers = (overall.activeCount || 0) + (overall.trialCount || 0) + (overall.pastDueCount || 0);

      // Total redemptions (count across merchant)
      const redemptionsQuery = query(
        collectionGroup(db, 'redemptions'),
        where('merchantId', '==', merchantId)
      );
      const redemptionsCountSnap = await getCountFromServer(redemptionsQuery);
      const redemptionCount = redemptionsCountSnap.data().count || 0;

      // Recent activity (most recent subscription)
      const recent = await subscriptionsService.getRecentSubscriptions(merchantId, 1);
      const lastActivity = recent[0]?.createdAt || undefined;

      return {
        totalSubscribers,
        activeSubscribers,
        monthlyRevenue,
        totalLocations,
        redemptionCount,
        lastActivity,
      };
    } catch (err) {
      console.warn('Failed to aggregate merchant stats, defaulting to zeros:', err);
      return {
        totalSubscribers: 0,
        activeSubscribers: 0,
        monthlyRevenue: 0,
        totalLocations: 0,
        redemptionCount: 0,
      };
    }
  }

  // Get sort field mapping
  private getSortField(sortBy?: string): string {
    switch (sortBy) {
      case 'subscribers':
        return 'totalSubscribers';
      case 'revenue':
        return 'monthlyRevenue';
      case 'activity':
        return 'lastActivity';
      case 'created':
        return 'createdAt';
      case 'name':
      default:
        return 'name';
    }
  }

  // Location Management
  async getLocations(merchantId: string): Promise<MerchantLocation[]> {
    try {
      const locationsRef = collection(db, this.COLLECTION, merchantId, this.LOCATIONS_SUBCOLLECTION);
      const snapshot = await getDocs(locationsRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<MerchantLocation, 'id'>),
      }));
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  async addLocation(merchantId: string, data: LocationFormData): Promise<string> {
    try {
      const locationsRef = collection(db, this.COLLECTION, merchantId, this.LOCATIONS_SUBCOLLECTION);
      const docRef = await addDoc(locationsRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding location:', error);
      throw error;
    }
  }

  async updateLocation(merchantId: string, locationId: string, data: Partial<LocationFormData>): Promise<void> {
    try {
      const locationRef = doc(db, this.COLLECTION, merchantId, this.LOCATIONS_SUBCOLLECTION, locationId);
      await updateDoc(locationRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  }

  async deleteLocation(merchantId: string, locationId: string): Promise<void> {
    try {
      const locationRef = doc(db, this.COLLECTION, merchantId, this.LOCATIONS_SUBCOLLECTION, locationId);
      await deleteDoc(locationRef);
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  }

  // Plan Management
  async getPlans(merchantId: string): Promise<MerchantPlan[]> {
    try {
      const plansRef = collection(db, this.COLLECTION, merchantId, this.PLANS_SUBCOLLECTION);
      const snapshot = await getDocs(plansRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<MerchantPlan, 'id'>),
      }));
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }

  async addPlan(merchantId: string, data: PlanFormData): Promise<string> {
    try {
      const plansRef = collection(db, this.COLLECTION, merchantId, this.PLANS_SUBCOLLECTION);
      const docRef = await addDoc(plansRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding plan:', error);
      throw error;
    }
  }

  async updatePlan(merchantId: string, planId: string, data: Partial<PlanFormData>): Promise<void> {
    try {
      const planRef = doc(db, this.COLLECTION, merchantId, this.PLANS_SUBCOLLECTION, planId);
      await updateDoc(planRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    }
  }

  async deletePlan(merchantId: string, planId: string): Promise<void> {
    try {
      const planRef = doc(db, this.COLLECTION, merchantId, this.PLANS_SUBCOLLECTION, planId);
      await deleteDoc(planRef);
    } catch (error) {
      console.error('Error deleting plan:', error);
      throw error;
    }
  }

  // Banking Management
  async getBankingInfo(merchantId: string): Promise<MerchantBankingInfo | null> {
    try {
      const docRef = doc(db, this.COLLECTION, merchantId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return (data.banking as MerchantBankingInfo | undefined) || null;
    } catch (error) {
      console.error('Error fetching banking info:', error);
      throw error;
    }
  }

  async updateBankingInfo(
    merchantId: string,
    bankingData: MerchantBankingInfo,
    accountNumber?: string,
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, merchantId);

      const trimmedAccountNumber = accountNumber?.replace(/\s+/g, '');
      const sanitizedRoutingNumber = bankingData.routingNumber?.replace(/\s+/g, '') || bankingData.routingNumber;

      if (trimmedAccountNumber) {
        const callable = httpsCallable<
          {
            merchantId: string;
            bankInfo: {
              routingNumber: string;
              accountNumber: string;
              accountName: string;
              accountType: 'checking' | 'savings';
            };
          },
          { success: boolean }
        >(functions, 'saveMerchantBankInfo');

        await callable({
          merchantId,
          bankInfo: {
            routingNumber: sanitizedRoutingNumber,
            accountNumber: trimmedAccountNumber,
            accountName: bankingData.accountHolderName,
            accountType: bankingData.accountType,
          },
        });
      }

      const { accountNumber: existingAccountNumber, ...rest } = bankingData;

      const maskedAccountNumber = trimmedAccountNumber
        ? trimmedAccountNumber.slice(-4)
        : existingAccountNumber?.slice(-4) ?? existingAccountNumber;

      const sanitizedBanking = removeUndefinedValues({
        ...rest,
        routingNumber: sanitizedRoutingNumber,
        hasEncryptedAccountNumber: trimmedAccountNumber
          ? true
          : bankingData.hasEncryptedAccountNumber,
        payoutMethod: 'manual',
        payoutSchedule: 'manual',
        ...(maskedAccountNumber ? { accountNumber: maskedAccountNumber } : {}),
        updatedAt: new Date(),
      });

      await updateDoc(docRef, {
        banking: sanitizedBanking,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating banking info:', error);
      throw error;
    }
  }

  async verifyBankAccount(merchantId: string, _depositAmounts: { amount1: number; amount2: number }): Promise<boolean> {
    try {
      void _depositAmounts;
      // In production, this would validate against actual micro-deposits
      // For now, we'll simulate verification by accepting any amounts
      const docRef = doc(db, this.COLLECTION, merchantId);
      
      const updateData = {
        'banking.verificationStatus': 'verified',
        'banking.verifiedAt': new Date(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error('Error verifying bank account:', error);
      throw error;
    }
  }
}

export const merchantService = new MerchantService();
